import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'
import SearchableSelect from '../SearchableSelect'

interface CustomsFinancialTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

const CURRENCIES = ['GBP', 'USD', 'EUR', 'CNY']

// Z73.18 appended spec (Q2): freight/insurance/delivery-location/VAT-adjustment fields for the
// eCustoms declaration generator -- none of these existed anywhere on jobs before. Gary's own
// spec calls out that this data is also needed elsewhere (Sage invoicing, BoxTop sync, finance
// reports), hence real job-level columns rather than something scoped only to eCustoms.
export default function CustomsFinancialTab({ jobData, setJobData, readOnly = false }: CustomsFinancialTabProps) {
  const [locodeLabel, setLocodeLabel] = useState<string>('')
  // The Incoterm now has ONE source: the Freight Terms dropdown on Ship Details (shipping_terms_id). This box only
  // displays it; the old free-text jobs.incoterm_code is retired (two columns for one fact is how KI26054 showed a
  // term on screen while the declaration said "not set").
  const termsId: string | null = jobData.sea_details?.shipping_terms_id || jobData.air_details?.shipping_terms_id || null
  const [termLabel, setTermLabel] = useState<string>('')
  useEffect(() => {
    if (!termsId) { setTermLabel(''); return }
    supabase.from('shipping_terms').select('code, description').eq('id', termsId).maybeSingle()
      .then(({ data }) => setTermLabel(data ? `${data.code} - ${data.description}` : ''))
  }, [termsId])

  // Resolves the currently-stored delivery_locode (a plain UN/LOCODE string, not a ports FK) to
  // a human-readable label on load -- same "pin the current selection's label" need
  // ShipDetailsTab's departure/arrival port pickers solve via pinnedPorts, just keyed by
  // un_locode here since jobs.delivery_locode stores the code itself, not a port id.
  useEffect(() => {
    const code = jobData.delivery_locode
    if (!code) { setLocodeLabel(''); return }
    supabase.from('ports').select('name, un_locode').eq('un_locode', code).eq('country', 'GB').maybeSingle()
      .then(({ data }) => setLocodeLabel(data ? `${data.un_locode} — ${data.name}` : code))
  }, [jobData.delivery_locode])

  const searchGbPorts = async (term: string) => {
    const cleaned = term.trim().replace(/[%_]/g, '')
    let query = supabase
      .from('ports')
      .select('un_locode, name')
      .eq('is_active', true)
      .eq('country', 'GB')
      .not('un_locode', 'is', null)
      .order('name')
      .limit(50)
    if (cleaned) query = query.or(`name.ilike.%${cleaned}%,un_locode.ilike.${cleaned}%`)
    const { data, error } = await query
    if (error) { console.error('[CustomsFinancialTab] searchGbPorts error:', error); return [] }
    return (data || []).map(p => ({ id: p.un_locode as string, label: `${p.un_locode} — ${p.name}` }))
  }

  const field = (key: string) => (value: unknown) => setJobData({ ...jobData, [key]: value })

  return (
    <div className="space-y-6">
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 p-4 rounded-lg space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-white">Customs &amp; Financial</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
          Feeds the eCustoms declaration (freight/insurance amounts, delivery location, Incoterm) as well as Sage invoicing and BoxTop sync.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Freight Cost</label>
            <div className="flex gap-2">
              <input
                type="number" step="0.01"
                value={jobData.freight_cost ?? ''}
                onChange={e => field('freight_cost')(e.target.value === '' ? null : parseFloat(e.target.value))}
                disabled={readOnly}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
              />
              <select
                value={jobData.freight_currency || 'GBP'}
                onChange={e => field('freight_currency')(e.target.value)}
                disabled={readOnly}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Insurance Amount</label>
            <div className="flex gap-2">
              <input
                type="number" step="0.01"
                value={jobData.insurance_amount ?? ''}
                onChange={e => field('insurance_amount')(e.target.value === '' ? null : parseFloat(e.target.value))}
                disabled={readOnly}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
              />
              <select
                value={jobData.insurance_currency || 'GBP'}
                onChange={e => field('insurance_currency')(e.target.value)}
                disabled={readOnly}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VAT Adjustment</label>
            <input
              type="number" step="0.01"
              value={jobData.vat_adjustment ?? 0}
              onChange={e => field('vat_adjustment')(e.target.value === '' ? 0 : parseFloat(e.target.value))}
              disabled
              title="No longer used: the declaration calculates the VAT Adjustment from the load type and gross mass on the eCustoms checklist"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Calculated on the eCustoms checklist (load type + gross mass); not entered here any more.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Incoterm</label>
            <input
              value={termLabel || (jobData.incoterm_code ? `${jobData.incoterm_code} (legacy value)` : '')}
              disabled
              placeholder="Not set"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Set on the Ship Details tab (Freight Terms). The declaration uses that value.</p>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Delivery Location (UK port, per Incoterm)</label>
            <SearchableSelect
              options={[]}
              onSearch={searchGbPorts}
              selectedOption={jobData.delivery_locode ? { id: jobData.delivery_locode, label: locodeLabel || jobData.delivery_locode } : undefined}
              value={jobData.delivery_locode || ''}
              onChange={field('delivery_locode')}
              disabled={readOnly}
              placeholder="Search UK port (code or name)..."
            />
          </div>
        </div>
      </div>
    </div>
  )
}
